import { observer } from 'mobx-react';
import { Card, Switch, Typography, message } from 'antd';
import { ThunderboltOutlined, LoadingOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

// Interactive twin of KpiTile, specialized for the per-agent skill-router toggle.
//
// The router flag has *behavioral* impact on every chat turn (pre-injects
// matching skills vs. advertises every skill the agent owns), so the tile is
// designed to make state legible at a glance and the action feel intentional:
//   • State is encoded three ways — accent (amber→ink lit when on), pill text
//     ("Routing enabled" / "Disabled"), and the antd Switch position
//   • The hint copy explains the *consequence* of the current state, not just
//     the state — "Pre-injects matching skills" vs "Sends every skill on each
//     turn (more tokens)"
//   • Save is optimistic: switch flips instantly, request flies in the
//     background, error reverts and toasts. No spinner blocks the UI.
//   • Tooltip on the info icon lets the user understand what the toggle does
//     before flipping it.

export const SkillRouterTile = observer(function SkillRouterTile() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const agent = detail.agent;
  const isDark = themeStore.mode === 'dark';
  const enabled = !!agent?.enableSkillRouter;
  const saving = detail.isSavingSkillRouter;

  // When the router is on, lean into the brand "ink" gradient (the active /
  // electrified look). When off, fall back to a muted slate gradient — same
  // tile dimensions and frame, just visually quiet.
  const bg = enabled
    ? isDark
      ? `linear-gradient(135deg, ${colors.ink[700]}55 0%, ${colors.violet[700]}33 100%)`
      : `linear-gradient(135deg, ${colors.ink[50]} 0%, ${colors.violet[50]} 100%)`
    : isDark
      ? `linear-gradient(135deg, ${colors.slate[700]}55 0%, ${colors.slate[800]} 100%)`
      : `linear-gradient(135deg, ${colors.slate[50]} 0%, #FFFFFF 100%)`;

  const border = enabled
    ? isDark ? colors.ink[600] + '88' : colors.ink[200]
    : isDark ? colors.slate[700] : colors.slate[200];

  const accentColor = enabled
    ? isDark ? colors.ink[100] : colors.ink[600]
    : isDark ? colors.slate[400] : colors.slate[500];

  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const headlineColor = isDark ? colors.slate[50] : colors.slate[900];
  const hintColor = isDark ? colors.slate[400] : colors.slate[500];

  const onToggle = async (next: boolean) => {
    try {
      await detail.setEnableSkillRouter(next);
      message.success(next ? 'Skill router enabled' : 'Skill router disabled');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not update skill router');
    }
  };

  return (
    <Card
      styles={{
        body: {
          padding: 18,
          minHeight: 120,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background 200ms ease, border-color 200ms ease',
        },
      }}
      style={{ border: 'none', cursor: 'default' }}
    >
      {/* Top row: label + info-icon + state pill on the right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: labelColor,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Skill router
          <HelpHotspot topic="agents.skill-router" variant="inline" label="Learn about the skill router" />
        </Text>
        <ThunderboltOutlined style={{ color: accentColor, opacity: enabled ? 0.9 : 0.4, fontSize: 18, lineHeight: 1, transition: 'opacity 200ms, color 200ms' }} />
      </div>

      {/* Value row: switch + headline state */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0 8px' }}>
        <Switch
          checked={enabled}
          onChange={onToggle}
          loading={saving}
          aria-label="Enable skill router"
        />
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: enabled ? accentColor : headlineColor,
            lineHeight: 1.1,
            transition: 'color 200ms',
          }}
        >
          {enabled ? 'Routing enabled' : 'Disabled'}
        </span>
        {saving && (
          <LoadingOutlined
            aria-label="Saving"
            style={{ fontSize: 13, color: hintColor }}
          />
        )}
      </div>

      {/* Hint — explains the consequence of the current state, not just the label */}
      <Text
        type="secondary"
        style={{ fontSize: 12, lineHeight: 1.45, color: hintColor }}
      >
        {enabled
          ? 'Pre-injects matching skills only — saves tokens, scales with skill count.'
          : 'Sends every skill on every turn — more tokens, but nothing gets filtered out.'}
      </Text>
    </Card>
  );
});
